import React, { useEffect, useState } from "react";
import moment from "moment";
import dayjs from "dayjs";
import styles from "../../../../../src/pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import CustomDatePicker from "../../../../components/CustomDatePicker/CustomDatePicker";

const renderCellWithDash = ({ cell }) => {
  const value = cell.getValue();
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

export const getStatusBadge = (statusText) => {
  if (!statusText) return "-";
  const key = String(statusText).toLowerCase().replace(/[-\s]/g, "");

  const classMap = {
    submitted: styles.statusSubmitted,
    approved: styles.statusApproved,
    rejected: styles.statusRejected,
    resubmitted: styles.statusResubmitted,
    completed: styles.statusCompleted,
  };

  return (
    <span className={`${styles.statusBadge} ${classMap[key] || ""}`}>
      {statusText}
    </span>
  );
};

const DateRangeFilter = ({ column, translations, onDateRangePicked }) => {
  // Read the current filter value from the column
  const filterValue = column.getFilterValue();
  const extStart =
    Array.isArray(filterValue) && filterValue[0] ? filterValue[0] : "";
  const extEnd =
    Array.isArray(filterValue) && filterValue[1] ? filterValue[1] : "";

  // ── LOCAL STATE ──
  // We maintain both dates locally so that whatever the user picks
  // (in either the start OR the end picker) stays visible in the UI
  // even if the other picker hasn't been filled yet.
  const [localStart, setLocalStart] = useState(extStart);
  const [localEnd, setLocalEnd] = useState(extEnd);

  // keep local state in sync if the external filter value is cleared
  // or changed from outside (e.g. the toggle resetting filters)
  useEffect(() => {
    setLocalStart(extStart);
    setLocalEnd(extEnd);
  }, [extStart, extEnd]);

  const startValue = localStart ? dayjs(localStart) : null;
  const endValue = localEnd ? dayjs(localEnd) : null;

  const commitFilter = (nextStart, nextEnd) => {
    // If both are empty → clear the filter
    if (!nextStart && !nextEnd) {
      column.setFilterValue(undefined);
    } else {
      // Always send BOTH values together — never drop the other one
      column.setFilterValue([nextStart || "", nextEnd || ""]);
    }
  };

  const handleStartChange = (dayjsDate) => {
    const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
    // update local state immediately so the user sees their pick
    setLocalStart(formatted);
    // keep the existing end date — DO NOT remove it
    commitFilter(formatted, localEnd);

    if (formatted && typeof onDateRangePicked === "function") {
      onDateRangePicked();
    }
  };

  const handleEndChange = (dayjsDate) => {
    const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
    // update local state immediately so the user sees their pick
    setLocalEnd(formatted);
    // keep the existing start date — DO NOT remove it
    commitFilter(localStart, formatted);

    if (formatted && typeof onDateRangePicked === "function") {
      onDateRangePicked();
    }
  };

  return (
    <div className={styles.dateRangeFilter}>
      <CustomDatePicker
        name={`${column.id}_start`}
        value={startValue}
        onChange={handleStartChange}
        placeholder={translations?.labels?.startDatePlaceholder || "Start Date"}
        format="DD/MM/YYYY"
        size="sm"
        isClearable={true}
        maxDate={endValue || undefined}
        className={styles.dateRangePicker}
      />
      <CustomDatePicker
        name={`${column.id}_end`}
        value={endValue}
        onChange={handleEndChange}
        placeholder={translations?.labels?.endDatePlaceholder || "End Date"}
        format="DD/MM/YYYY"
        size="sm"
        isClearable={true}
        minDate={startValue || undefined}
        className={styles.dateRangePicker}
      />
    </div>
  );
};

const ApprovedStatusCell = ({ row, translations, onChangeStatus }) => {
  const currentLabel = row?.original?.status_text || "";
  const isCompleted =
    String(currentLabel).toLowerCase() === "completed" ||
    Number(row?.original?.status) === 4 ||
    Number(row?.original?.status) === 5;

  const handleSelectChange = (e) => {
    const nextLabel = e.target.value;
    if (isCompleted) return;
    if (!nextLabel || nextLabel === currentLabel) return;
    if (
      String(currentLabel).toLowerCase() === "approved" &&
      String(nextLabel).toLowerCase() === "completed"
    ) {
      onChangeStatus(row.original, nextLabel);
    }
  };

  // When completed, lock the select to "Completed"
  // When approved, default select shows "Approved"
  const selectValue = isCompleted ? "Completed" : "Approved";

  return (
    <select
      className={styles.statusSelect}
      value={selectValue}
      onChange={handleSelectChange}
      disabled={isCompleted}
    >
      <option value="Approved">
        {translations?.labels?.statusApproved || "Approved"}
      </option>
      <option value="Completed">
        {translations?.labels?.statusCompleted || "Completed"}
      </option>
    </select>
  );
};

export const buildLessonPlanColumns = (opts) => {
  const {
    translations = {},
    topicOptions = [],
    subTopicOptions = [],
    includeTopic = true, // 👈 default true so other screens are unaffected
    includeSubTopic = true,
    includeSection = false,
    includePeriod = false,
    includeDay = false,
    includeScheduledDate = false,
    includeStatus = false,
    includeCreatedBy = false, // 👈 NEW — Previous Years tab
    includeApprovedBy = false, // 👈 NEW — Previous Years tab
    approvedStatusCell = false,
    onDateRangePicked,
    onChangeStatus,
  } = opts;

  const cols = [
    {
      accessorKey: "book",
      header: translations?.labels?.subject || "Subject",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.subject || "Subject",
      },
      Cell: renderCellWithDash,
    },
    {
      accessorKey: "class_code",
      header: translations?.labels?.class || "Class",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.class || "Class",
      },
      Cell: renderCellWithDash,
    },
  ];

  if (includeSection) {
    cols.push({
      accessorKey: "section",
      header: translations?.labels?.section || "Section",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.section || "Section",
      },
      Cell: renderCellWithDash,
    });
  }
  if (includePeriod) {
    cols.push({
      accessorKey: "periods",
      header: translations?.labels?.perios || "Period",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.perios || "Period",
      },
      Cell: renderCellWithDash,
    });
  }
  if (includeDay) {
    cols.push({
      accessorKey: "dayid",
      header: translations?.labels?.day || "Day",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.day || "Day",
      },
      Cell: renderCellWithDash,
    });
  }
  if (includeTopic) {
    cols.push({
      accessorKey: "topic",
      header: translations?.labels?.topic || "Topic",
      enableSorting: true,
      enableColumnFilter: true,
      filterVariant: "select",
      filterSelectOptions: topicOptions.map((t) => ({ text: t, value: t })),
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.topic || "Topic",
      },
      Cell: renderCellWithDash,
    });
  }

  if (includeSubTopic) {
    cols.push({
      accessorKey: "sub_topic",
      header: translations?.labels?.subTopic || "Sub Topic",
      enableSorting: true,
      enableColumnFilter: true,
      filterVariant: "select",
      filterSelectOptions: subTopicOptions.map((t) => ({ text: t, value: t })),
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.subTopic || "Sub Topic",
      },
      Cell: renderCellWithDash,
    });
  }

  cols.push({
    accessorKey: "medium",
    header: translations?.labels?.medium || "Medium",
    enableSorting: true,
    enableColumnFilter: true,
    muiFilterTextFieldProps: {
      placeholder: translations?.labels?.medium || "Medium",
    },
    Cell: renderCellWithDash,
  });

  if (includeScheduledDate) {
    cols.push({
      accessorKey: "scheduled_date",
      header: translations?.labels?.scheduledDate || "Scheduled Date",
      enableSorting: true,
      enableColumnFilter: true,
      Filter: ({ column }) => (
        <DateRangeFilter
          column={column}
          translations={translations}
          onDateRangePicked={onDateRangePicked}
        />
      ),
      Cell: ({ cell }) => {
        const v = cell.getValue();
        if (!v) return "-";
        return moment(v).format("DD/MM/YYYY");
      },
      size: 280,
    });
  }

  if (includeStatus) {
    cols.push({
      accessorKey: "status_text",
      header: translations?.labels?.status || "Status",
      enableSorting: true,
      enableColumnFilter: !approvedStatusCell,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.status || "Status",
      },
      Cell: approvedStatusCell
        ? ({ row }) => (
            <ApprovedStatusCell
              row={row}
              translations={translations}
              onChangeStatus={onChangeStatus}
            />
          )
        : ({ cell }) => getStatusBadge(cell.getValue()),
      size: 220,
    });
  }

  if (includeCreatedBy) {
    cols.push({
      accessorKey: "created_by",
      header: translations?.labels?.createdBy || "Created By",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.createdBy || "Created By",
      },
      Cell: renderCellWithDash,
    });
  }

  if (includeApprovedBy) {
    cols.push({
      accessorKey: "approved_by",
      header: translations?.labels?.approvedBy || "Approved By",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.approvedBy || "Approved By",
      },
      Cell: renderCellWithDash,
    });
  }

  return cols;
};
